import React, { useState, useEffect } from "react";
import { fetchUserDepartment, deleteEmployeeById } from "../services/airtable";
import {
  getFirestore,
  collection,
  query,
  where,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
  addDoc,
} from "firebase/firestore";
import "../styles/styles.css";

const UserManagement = () => {
  const [userData, setUserData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedDepartment, setSelectedDepartment] = useState("");
  const [newUsers, setNewUsers] = useState([{ name: "", email: "", department: "" }]);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const firestore = getFirestore();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const airtableData = await fetchUserDepartment();
        const validData = airtableData.filter((record) => record.ImageEmail);
    
        const userAccountsCollection = collection(firestore, "user_accounts");
        const existingDocs = await getDocs(userAccountsCollection);
        const existingEmails = existingDocs.docs.map((doc) => doc.data().ImageEmail);
    
        
        for (const record of validData) {
          if (!existingEmails.includes(record.ImageEmail.trim())) {
            await addDoc(userAccountsCollection, record);
          }
        }
    
        const firestoreData = existingDocs.docs.map((doc) => ({
          id: doc.id, 
          ...doc.data(),
        }));
        setUserData(firestoreData.filter((user) => user.Employee && user.ImageEmail && user.Department));
        setLoading(false);
      } catch (err) {
        setError("Failed to load user data.");
        setLoading(false);
      }
    };
    

    fetchData();
  }, [firestore]);

  const handleSort = () => {
    const sortedData = [...userData].sort((a, b) => {
      const deptA = String(a.Department || "").toLowerCase();
      const deptB = String(b.Department || "").toLowerCase();
      return sortOrder === "asc"
        ? deptA.localeCompare(deptB)
        : deptB.localeCompare(deptA);
    });
    setUserData(sortedData);
    setSortOrder(sortOrder === "asc" ? "desc" : "asc");
  };

  const handleEdit = (id) => {
    const user = userData.find((user) => user.id === id); 
    if (user) {
      setSelectedUser(user);
      setSelectedDepartment(user.Department || "");
      setIsEditModalOpen(true);
    }
  };
  

  const updateUserDepartment = async () => {
    if (!selectedUser) return;

    try {
      const userRef = doc(firestore, "user_accounts", selectedUser.id);
      await updateDoc(userRef, { Department: selectedDepartment });

      const updatedData = userData.map((user) =>
        user.id === selectedUser.id
          ? { ...user, Department: selectedDepartment }
          : user
      );

      setUserData(updatedData);
      setIsEditModalOpen(false);
    } catch (err) {
      setError("Failed to update department.");
    }
  };

  const handleDelete = (id) => {
    const user = userData.find((user) => user.id === id); 
    if (user) {
      setSelectedUser(user);
      setIsDeleteModalOpen(true);
    } else {
      setError("User not found for deletion.");
    }
  };
  

  const deleteUser = async () => {
    if (!selectedUser) {
      setError("No user selected for deletion.");
      return;
    }
  
    try {
  
      const userDocRef = doc(firestore, "user_accounts", selectedUser.id);
  
     
      await deleteDoc(userDocRef);
  
      const updatedData = userData.filter((user) => user.id !== selectedUser.id);
      setUserData(updatedData);
      setIsDeleteModalOpen(false);
    } catch (err) {
      setError("An error occurred while deleting the user.");
      console.error(err);
    }
  };
  
  const addNewUsers = async () => {
    const userAccountsCollection = collection(firestore, "user_accounts");
    const newValidUsers = [];

    try {
      for (const user of newUsers) {
        const { name, email, department } = user;
        if (!name || !email || !department) continue;

        const q = query(userAccountsCollection, where("ImageEmail", "==", email.trim()));
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
          alert(`The email ${email} is already registered.`);
          continue;
        }

        const newUser = { Employee: name, ImageEmail: email, Department: department };
        const newDoc = await addDoc(userAccountsCollection, newUser);

        newValidUsers.push({ id: newDoc.id, ...newUser });
      }

      setUserData([...userData, ...newValidUsers]);
      setNewUsers([{ name: "", email: "", department: "" }]);
      setIsAddModalOpen(false);
    } catch (err) {
      setError("Failed to add new users.");
    }
  };

  const handleNewUserChange = (index, field, value) => {
    const updatedUsers = [...newUsers];
    updatedUsers[index][field] = value;
    setNewUsers(updatedUsers);
  };

  const addNewUserRow = () => {
    setNewUsers([...newUsers, { name: "", email: "", department: "" }]);
  };

  const filteredUserData = userData.filter(
    (user) =>
      (user?.Employee || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
      (user?.ImageEmail || "").toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;


    return (
      <div className="user-management">
        <h1>User Management</h1>

        <button className="add-btn" onClick={() => setIsAddModalOpen(true)}>
          + Add New Users
        </button>

        <div className="search-container">
          <input
            type="text"
            className="search-input"
            placeholder="Search by name or email"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <button onClick={handleSort} className="sort-btn">
          Sort by Department ({sortOrder === "asc" ? "A-Z" : "Z-A"})
        </button>

        <table className="user-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Employee Name</th>
              <th>Email</th>
              <th>Department</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUserData.map((user, index) => (
              <tr key={index}>
                <td>{index + 1}</td>
                <td>{user.Employee || "N/A"}</td>
                <td>{user.ImageEmail || "N/A"}</td>
                <td>{user.Department || "N/A"}</td>
                <td>
                <button className="edit-btn" onClick={() => handleEdit(user.id)}>
  Edit
</button>

                  <button
               className="delete-btn"
               onClick={() => handleDelete(user.id)} 
               >
              Delete
              </button>

                </td>
              </tr>
            ))}
          </tbody>
        </table>

    
        {isEditModalOpen && (
          <div className="modal-overlay">
            <div className="modal-content">
              <h3>Edit Department</h3>
              <p>
                <strong>Employee:</strong> {selectedUser?.Employee || "N/A"}
              </p>
              <p>
                <strong>Email:</strong> {selectedUser?.ImageEmail || "N/A"}
              </p>
              <select
                value={selectedDepartment}
                onChange={(e) => setSelectedDepartment(e.target.value)}
              >
                <option value="">Select Department</option>
                <option value="Sales">Sales</option>
                <option value="PM">PM</option>
                <option value="BDM">BDM</option>
                <option value="Admin">Admin</option>
              </select>
              <button onClick={updateUserDepartment}>Update</button>
              <button onClick={() => setIsEditModalOpen(false)}>Cancel</button>
            </div>
          </div>
        )}

        {isDeleteModalOpen && (
          <div className="modal-overlay">
            <div className="modal-content">
              <h3>Confirm Deletion</h3>
              <p>
                Are you sure you want to delete{" "}
                <strong>{selectedUser?.Employee || "this user"}</strong>?
              </p>
              <button onClick={deleteUser}>Yes, Delete</button>
              <button onClick={() => setIsDeleteModalOpen(false)}>Cancel</button>
            </div>
          </div>
        )}

        {isAddModalOpen && (
          <div className="add-user-modal show">
            <div className="add-user-modal-content">
              <h3>Add New Users</h3>
              {newUsers.map((user, index) => (
                <div key={index} className="add-user-row">
                  <input
                    type="text"
                    placeholder="Employee Name"
                    value={user.name}
                    onChange={(e) =>
                      handleNewUserChange(index, "name", e.target.value)
                    }
                  />
                  <input
                    type="email"
                    placeholder="Email"
                    value={user.email}
                    onChange={(e) =>
                      handleNewUserChange(index, "email", e.target.value)
                    }
                  />
                  <select
                    value={user.department}
                    onChange={(e) =>
                      handleNewUserChange(index, "department", e.target.value)
                    }
                  >
                    <option value="">Select Department</option>
                    <option value="Sales">Sales</option>
                    <option value="PM">PM</option>
                    <option value="BDM">BDM</option>
                    <option value="Admin">Admin</option>
                  </select>
                </div>
              ))}
              <button onClick={addNewUserRow}>+ Add Another User</button>
              <button onClick={addNewUsers}>Submit</button>
              <button onClick={() => setIsAddModalOpen(false)}>Cancel</button>
            </div>
          </div>
        )}
      </div>
    );
  };

  export default UserManagement;
